// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { SnackMessage } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import {
  AwardsPayload,
  GetAwardsRequestState,
} from "../../utils/types";

import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: GetAwardsRequestState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: [],
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const getAwards = createAsyncThunk(
  "exams/getAwards",
  async (email: string, { dispatch }) => {
    return new Promise<{ awards: AwardsPayload[] }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(
            AppConfig.serviceUrls.getContact +
            "/" + 
            email + 
            "/awards"
          )
          .then((resp) => {
            if (resp.status == 200) {
              resolve({ awards: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            dispatch(
              enqueueSnackbarMessage({
                message: SnackMessage.error.awards,
                type: "error",
              })
            );
            reject(error);
          });
      }
    );
  }
);

export const AwardsSlice = createSlice({
  name: "award",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAwards.pending, (state, action) => {
        state.stateMessage = "Loading Awards";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Fetching Awards Data";
      })
      .addCase(getAwards.fulfilled, (state, action) => {
        state.response = action.payload.awards.sort((a, b) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(getAwards.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      });
  },
});

export default AwardsSlice.reducer;
